/**
 * Las dos decisiones de la UI del borrado diferido, en funciones puras.
 *
 * POR QUÉ FUERA DEL COMPONENTE: montar `KnowledgeDocuments` entero exige sus
 * contextos, `httpsCallable` y Firestore. Es el mismo motivo por el que
 * `rutaDeEnvio` y `CuerpoMensaje` viven aparte en este repositorio.
 *
 * EL DEFECTO QUE CIERRAN. R2b introdujo el borrado diferido: un documento grande
 * queda en `embedding_status: 'deleting'` mientras el barrido programado termina
 * con sus chunks. La UI no conocía ese estado, así que el badge caía al default
 * y decía **«Pendiente»** — que es lo contrario de lo que pasa: el documento no
 * espera a indexarse, se está yendo.
 */

/** Estados que la UI sabe nombrar. Todo lo demás es desconocido, no «pendiente». */
export const ESTADOS = {
  pending: { color: 'orange', clave: 'documents.statusPending' },
  processing: { color: 'blue', clave: 'documents.statusProcessing' },
  completed: { color: 'green', clave: 'documents.statusCompleted' },
  failed: { color: 'red', clave: 'documents.statusFailed' },
  deleting: { color: 'orange', clave: 'documents.statusDeleting' },
};

/**
 * Qué badge corresponde a un `embedding_status`.
 *
 * El default NO es `pending`. Un estado que no conocemos no es «pendiente»:
 * decirlo así convirtió el borrado en indexación a ojos del cliente durante todo
 * R2b, y el siguiente estado que alguien añada al servidor volvería a mentir en
 * silencio. Es mejor admitir que no se sabe.
 */
export function estadoDeDocumento(status) {
  return ESTADOS[status] || { color: 'gray', clave: 'documents.statusUnknown' };
}

/**
 * ¿La papelera está deshabilitada para este documento?
 *
 * Dos fuentes, y hacen falta las dos: `enVuelo` es la llamada desde ESTA pestaña,
 * y `embedding_status === 'deleting'` es el estado del servidor — que sobrevive a
 * recargar la página y lo ven también los demás miembros de la organización. Sin
 * el segundo, la papelera se rehabilita al refrescar y se puede pedir el borrado
 * de algo que ya se está borrando.
 */
export function borradoEnCurso(doc, enVuelo) {
  return enVuelo === doc.id || doc.embedding_status === 'deleting';
}

/**
 * Qué mensaje mostrar tras pedir el borrado.
 *
 * El aviso de «continúa en segundo plano» sale SÓLO SI TARDA, y no se adivina:
 * lo decide la lista recargada. El callable de R2b borra hasta 2.000 chunks de
 * una vez; si caben, el documento ya no está y decir que sigue borrándose sería
 * mentir. Si sigue ahí en `deleting`, es que se difirió.
 *
 * SIN PORCENTAJE, deliberadamente: con la ruta diferida el borrado puede tardar
 * horas, y una barra que no avanza es peor que no ponerla.
 */
export function mensajeDeBorrado(documentosTrasBorrar, docId) {
  const sigue = (documentosTrasBorrar || []).some(
    (d) => d.id === docId && d.embedding_status === 'deleting'
  );
  return sigue ? 'documents.deleteDeferred' : 'documents.deleteSuccess';
}
